import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatTabsModule } from '@angular/material/tabs';
import { forkJoin } from 'rxjs';
import {
  TableColumn,
  TableEmptyState,
  TableFilter,
  TableInspector,
  TablePageAction,
  TablePageActionEvent,
  TablePageComponent,
  TableSummaryItem,
  TableTone
} from 'src/app/con-x/table/table-page.component';
import { AddressBook, AddressBookEntry } from './address-book.models';
import { addressBookErrorMessage } from './address-book-name-dialog.component';
import { AddressBookService } from './address-book.service';

@Component({
  selector: 'app-platform-address-book',
  standalone: true,
  imports: [MatTabsModule, TablePageComponent],
  templateUrl: './platform-address-book.component.html',
  styleUrls: ['./platform-address-book.component.scss']
})
export class PlatformAddressBookComponent implements OnInit {
  addressBooks: AddressBook[] = [];
  entries: AddressBookEntry[] = [];
  selectedEntry: AddressBookEntry | null = null;
  selectedAddressBook: AddressBook | null = null;
  selectedAddressBookFilter: AddressBook | null = null;
  activeTabIndex = 0;
  loading = false;
  errorMessage = '';

  private readonly addressBooksById = new Map<number, AddressBook>();

  readonly entryColumns: readonly TableColumn<AddressBookEntry>[] = [
    {
      key: 'type', label: 'Typ', type: 'badge', value: entry => this.entryTypeLabel(entry),
      tone: entry => this.entryTypeTone(entry), width: '8rem'
    },
    { key: 'primaryIdentifier', label: 'Kontakt' },
    { key: 'addressBook', label: 'Adressbuch', value: entry => this.addressBookName(entry) },
    { key: 'owner', label: 'Eigentümer', value: entry => this.entryOwnerName(entry),
      secondaryValue: entry => this.entryOwnerReference(entry) },
    { key: 'email', label: 'E-Mail', value: entry => entry.emails?.[0], emptyText: '—' },
    { key: 'updatedAt', label: 'Geändert', type: 'datetime' }
  ];

  readonly addressBookColumns: readonly TableColumn<AddressBook>[] = [
    { key: 'name', label: 'Adressbuch' },
    { key: 'owner', label: 'Eigentümer', value: book => this.ownerName(book),
      secondaryValue: book => this.ownerReference(book) },
    { key: 'entryCount', label: 'Kontakte', type: 'number', align: 'end' },
    { key: 'updatedAt', label: 'Geändert', type: 'datetime' }
  ];

  readonly entryFilters: readonly TableFilter<AddressBookEntry>[] = [
    {
      id: 'type', label: 'Kontakttyp', allLabel: 'Alle Kontakte',
      options: [
        { label: 'Personen', value: 'PERSON' },
        { label: 'Organisationen', value: 'ORGANISATION' }
      ]
    },
    {
      id: 'ownerType', label: 'Bereich', allLabel: 'Alle Bereiche',
      value: entry => this.addressBooksById.get(entry.addressBookId)?.ownerType,
      options: [
        { label: 'Persönlich', value: 'USER' },
        { label: 'Organisation', value: 'ORGANISATION' }
      ]
    }
  ];

  readonly addressBookFilters: readonly TableFilter<AddressBook>[] = [{
    id: 'ownerType', label: 'Bereich', allLabel: 'Alle Bereiche',
    options: [
      { label: 'Persönlich', value: 'USER' },
      { label: 'Organisation', value: 'ORGANISATION' }
    ]
  }];

  readonly entryEmptyState: TableEmptyState = {
    icon: 'contact_page',
    title: 'Keine Kontakte vorhanden',
    description: 'Auf der Plattform wurden noch keine Adressbucheinträge angelegt.'
  };

  readonly addressBookEmptyState: TableEmptyState = {
    icon: 'menu_book',
    title: 'Keine Adressbücher vorhanden',
    description: 'Auf der Plattform wurden noch keine Adressbücher angelegt.'
  };

  get visibleEntries(): AddressBookEntry[] {
    const addressBookId = this.selectedAddressBookFilter?.addressBookId;
    return addressBookId == null
      ? this.entries
      : this.entries.filter(entry => entry.addressBookId === addressBookId);
  }

  get contactTitle(): string {
    return this.selectedAddressBookFilter
      ? `Kontakte in ${this.selectedAddressBookFilter.name}`
      : 'Plattformweites Adressbuch';
  }

  get contactDescription(): string {
    return this.selectedAddressBookFilter
      ? `${this.ownerName(this.selectedAddressBookFilter)} · ${this.ownerReference(this.selectedAddressBookFilter)}`
      : 'Alle Kontakte aus persönlichen und organisationsbezogenen Adressbüchern.';
  }

  get contactActions(): TablePageAction[] {
    return this.selectedAddressBookFilter ? [{
      id: 'showAllContacts',
      label: 'Alle Kontakte',
      icon: 'close',
      appearance: 'stroked'
    }] : [];
  }

  get entrySummaryItems(): readonly TableSummaryItem<AddressBookEntry>[] {
    return [
      { label: 'Kontakte', value: this.visibleEntries.length, icon: 'contacts' },
      { label: 'Personen', value: this.visibleEntries.filter(entry => entry.type === 'PERSON').length, icon: 'person' },
      {
        label: 'Organisationen',
        value: this.visibleEntries.filter(entry => entry.type === 'ORGANISATION').length,
        icon: 'business'
      }
    ];
  }

  get addressBookSummaryItems(): readonly TableSummaryItem<AddressBook>[] {
    return [
      { label: 'Adressbücher', value: this.addressBooks.length, icon: 'menu_book' },
      {
        label: 'Persönlich',
        value: this.addressBooks.filter(book => book.ownerType === 'USER').length,
        icon: 'person'
      },
      {
        label: 'Organisationen',
        value: this.addressBooks.filter(book => book.ownerType === 'ORGANISATION').length,
        icon: 'business'
      }
    ];
  }

  get entryInspector(): TableInspector<AddressBookEntry> {
    return {
      ariaLabel: 'Plattformweite Kontaktdetails',
      title: 'primaryIdentifier',
      subtitle: entry => this.entryTypeLabel(entry),
      description: entry => this.addressBookName(entry),
      icon: entry => entry.type === 'PERSON' ? 'person' : 'business',
      tone: entry => this.entryTypeTone(entry),
      fields: [
        { label: 'Adressbuch', value: entry => this.addressBookName(entry) },
        { label: 'Eigentümer', value: entry => this.entryOwnerName(entry) },
        { label: 'Eigentümer-ID', value: entry => this.entryOwnerReference(entry), monospace: true },
        { label: 'E-Mail', value: entry => entry.emails?.join(', '), emptyText: '—' },
        {
          label: 'Telefon',
          value: entry => entry.phoneNumbers?.map(phone => `${phone.countryCode} ${phone.number}`).join(', '),
          emptyText: '—'
        },
        { label: 'DIDs', value: entry => entry.dids?.join(', '), emptyText: '—', monospace: true }
      ]
    };
  }

  constructor(
    private readonly addressBookService: AddressBookService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.refresh();
  }

  refresh(): void {
    this.loading = true;
    this.errorMessage = '';
    forkJoin({
      addressBooks: this.addressBookService.getPlatformAddressBooks(),
      entries: this.addressBookService.getPlatformEntries()
    }).subscribe({
      next: ({ addressBooks, entries }) => {
        this.addressBooks = addressBooks;
        this.entries = entries;
        this.addressBooksById.clear();
        addressBooks.forEach(book => this.addressBooksById.set(book.addressBookId, book));
        if (this.selectedAddressBookFilter) {
          this.selectedAddressBookFilter = this.addressBooksById.get(
            this.selectedAddressBookFilter.addressBookId
          ) ?? null;
          this.selectedAddressBook = this.selectedAddressBookFilter;
        }
        this.loading = false;
        this.changeDetectorRef.markForCheck();
      },
      error: error => {
        this.loading = false;
        this.errorMessage = addressBookErrorMessage(
          error,
          'Das plattformweite Adressbuch konnte nicht geladen werden.'
        );
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  openAddressBook(addressBook: AddressBook | null): void {
    if (!addressBook) return;
    this.selectedAddressBook = addressBook;
    this.selectedAddressBookFilter = addressBook;
    this.selectedEntry = null;
    this.activeTabIndex = 0;
  }

  handleContactPageAction(event: TablePageActionEvent): void {
    if (event.actionId !== 'showAllContacts') return;
    this.selectedAddressBookFilter = null;
    this.selectedAddressBook = null;
    this.selectedEntry = null;
  }

  private addressBookName(entry: AddressBookEntry): string {
    return this.addressBooksById.get(entry.addressBookId)?.name ?? `Adressbuch #${entry.addressBookId}`;
  }

  private entryOwnerName(entry: AddressBookEntry): string {
    const book = this.addressBooksById.get(entry.addressBookId);
    return book ? this.ownerName(book) : 'Unbekannt';
  }

  private entryOwnerReference(entry: AddressBookEntry): string {
    const book = this.addressBooksById.get(entry.addressBookId);
    return book ? this.ownerReference(book) : '—';
  }

  private ownerName(book: AddressBook): string {
    return book.ownerName?.trim() || this.ownerTypeLabel(book.ownerType);
  }

  private ownerReference(book: AddressBook): string {
    return `${this.ownerTypeLabel(book.ownerType)} #${book.ownerId}`;
  }

  private ownerTypeLabel(ownerType: AddressBook['ownerType']): string {
    return ownerType === 'ORGANISATION' ? 'Organisation' : 'Persönlich';
  }

  private entryTypeLabel(entry: AddressBookEntry): string {
    return entry.type === 'PERSON' ? 'Person' : 'Organisation';
  }

  private entryTypeTone(entry: AddressBookEntry): TableTone {
    return entry.type === 'PERSON' ? 'primary' : 'accent';
  }
}
